import React, { useState, useMemo, useCallback } from 'react';
import { Box, Text, VStack, HStack, Badge, Collapse } from '@chakra-ui/react';
import { SimilarityMatch } from '../types';
import '../styles/HospitalSimilarity.css';

const CONFIDENCE_THRESHOLDS = {
  HIGH: 0.9,
  MEDIUM: 0.8,
} as const;

const SCORE_THRESHOLD = 0.8;

interface SimilarityMatchesProps {
  matches: SimilarityMatch[];
  humanMatched?: SimilarityMatch[];
  hospitalStatus: string;
  hospitalId: string;
  selectedMatchId?: string;
  onSelectMatch?: (hospitalId: string, matchId: string) => void;
}

const SimilarityMatches: React.FC<SimilarityMatchesProps> = ({ 
  matches, 
  humanMatched,
  hospitalStatus, 
  hospitalId, 
  selectedMatchId, 
  onSelectMatch 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getConfidenceLevel = useCallback((avgScore: number) => {
    if (avgScore > CONFIDENCE_THRESHOLDS.HIGH) return { level: 'high', colorScheme: 'red' };
    if (avgScore > CONFIDENCE_THRESHOLDS.MEDIUM) return { level: 'medium', colorScheme: 'orange' };
    return { level: 'low', colorScheme: 'yellow' };
  }, []);

  const getScoreBadgeColor = useCallback((score: number) => {
    return score >= SCORE_THRESHOLD ? 'green' : 'gray';
  }, []);

  const displayMatches = useMemo(() => {
    if (hospitalStatus === 'matched') {
      return humanMatched && humanMatched.length > 0 
        ? humanMatched.slice(0, 1) 
        : matches.slice(0, 1);
    }
    return matches;
  }, [hospitalStatus, matches, humanMatched]);



  return (
    <Box mt={4} bg="gray.50" borderRadius="lg" p={4}>
      <HStack
        justify="space-between"
        cursor="pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Text fontWeight="semibold" color="gray.800">
          🎯 {hospitalStatus === 'matched' ? 'Matched Hospital' : `Nearest Matched Hospitals (${matches.length})`}
        </Text>
        <Text color="gray.400" fontSize="sm">
          {isExpanded ? '▲' : '▼'}
        </Text>
      </HStack>

      <Collapse in={isExpanded}>
        <VStack spacing={3} mt={3}>
          {displayMatches.map((match, index) => {
            const avgScore = match.overall_similarity;
            const confidence = getConfidenceLevel(avgScore);

            return (
              <Box 
                key={match.id} 
                bg="white" 
                borderRadius="lg" 
                p={4} 
                border="1px" 
                borderColor="gray.200" 
                borderLeft="4px solid" 
                borderLeftColor={
                  avgScore > CONFIDENCE_THRESHOLDS.HIGH 
                    ? '#e74c3c' 
                    : avgScore > CONFIDENCE_THRESHOLDS.MEDIUM 
                    ? '#f39c12' 
                    : '#f1c40f'
                } 
                w="full"
              >
                <HStack justify="space-between" align="flex-start" mb={2}>
                  <HStack spacing={3}>
                    {(hospitalStatus === 'doubtful' || hospitalStatus === 'new_hospital') && (
                      <input
                        type="radio"
                        name={`hospital-select-${hospitalId}`}
                        checked={selectedMatchId === match.id}
                        onChange={() => onSelectMatch?.(hospitalId, match.id)}
                        className="radio-input"
                      />
                    )}
                    <Text fontWeight="bold" color="red.600">
                      {(avgScore * 100).toFixed(1)}%
                    </Text>
                    <Badge colorScheme={confidence.colorScheme} fontSize="xs">
                      {confidence.level}
                    </Badge>
                  </HStack>
                </HStack>
                
                <Text fontWeight="semibold" color="gray.800" mb={1}>
                  {match.name}
                </Text>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  {match.address}, {match.city}, {match.state} - {match.pincode}
                </Text>
                
                <HStack spacing={2} flexWrap="wrap">
                  <Badge colorScheme={getScoreBadgeColor(match.name_similarity)} fontSize="xs">
                    Name: {(match.name_similarity * 100).toFixed(1)}%
                  </Badge>
                  <Badge colorScheme={getScoreBadgeColor(match.address_similarity)} fontSize="xs">
                    Address: {(match.address_similarity * 100).toFixed(1)}%
                  </Badge>
                  <Badge colorScheme={getScoreBadgeColor(match.city_district_state_similarity)} fontSize="xs">
                    Location: {(match.city_district_state_similarity * 100).toFixed(1)}%
                  </Badge>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      </Collapse>
    </Box>
  );
};

export default SimilarityMatches;